import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Button, Typography, Container } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

/* ── Decorative sparkle ──────────────────────────────────────────── */
const Sparkle = ({ size = 16, x, y, delay = 0, color = "#8B5CF6" }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className="absolute pointer-events-none"
    style={{ left: x, top: y }}
    animate={{
      opacity: [0, 0.7, 0],
      scale: [0.5, 1.2, 0.5],
      rotate: [0, 180, 360],
    }}
    transition={{ duration: 5, repeat: Infinity, delay }}
  >
    <path
      d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z"
      fill={color}
      fillOpacity="0.5"
    />
  </motion.svg>
);

/* ── Thin concentric arcs ──────────────────────────────────────────── */
const DecorativeArcs = () => (
  <svg
    className="absolute top-[10%] right-[2%] w-[40%] h-[70%] pointer-events-none opacity-[0.1]"
    viewBox="0 0 400 400"
    fill="none"
  >
    {[180, 250, 320].map((r, i) => (
      <motion.circle
        key={i}
        cx="400"
        cy="200"
        r={r}
        stroke={["#C084FC", "#8B5CF6", "#7393B3"][i]}
        strokeWidth="0.7"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 3, delay: 0.5 + i * 0.3 }}
      />
    ))}
  </svg>
);

/* ── Animated EEG wave along bottom — richer & multi-layered ────── */
const EEGWave = () => (
  <svg
    className="absolute bottom-0 left-0 w-full h-20 pointer-events-none"
    viewBox="0 0 1000 60"
    preserveAspectRatio="none"
  >
    <motion.path
      d="M0,30 Q50,30 80,18 T140,42 T200,14 T260,46 T320,24 T380,36 T440,12 T500,48 T560,20 T620,40 T680,18 T740,34 T800,26 T860,42 T920,14 T980,36 T1040,24 T1100,46 T1160,18 T1220,34 T1280,26 T1340,36 T1400,30"
      fill="none"
      stroke="url(#heroEEGBright)"
      strokeWidth="2"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 4, ease: "easeOut" }}
      style={{ opacity: 0.25 }}
    />
    <motion.path
      d="M0,35 Q60,35 90,22 T150,45 T220,18 T290,42 T360,28 T430,38 T500,15 T570,44 T640,22 T710,40 T780,20 T850,38 T920,24 T990,42 T1060,16 T1130,40 T1200,22 T1270,36 T1340,28 T1400,35"
      fill="none"
      stroke="url(#heroEEGSecondary)"
      strokeWidth="1.5"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 4.5, ease: "easeOut", delay: 0.5 }}
      style={{ opacity: 0.15 }}
    />
    <defs>
      <linearGradient id="heroEEGBright" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#7393B3" stopOpacity="0" />
        <stop offset="20%" stopColor="#8B5CF6" />
        <stop offset="50%" stopColor="#C084FC" />
        <stop offset="80%" stopColor="#8B5CF6" />
        <stop offset="100%" stopColor="#7393B3" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="heroEEGSecondary" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#C084FC" stopOpacity="0" />
        <stop offset="30%" stopColor="#EC4899" stopOpacity="0.6" />
        <stop offset="70%" stopColor="#8B5CF6" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#7393B3" stopOpacity="0" />
      </linearGradient>
    </defs>
  </svg>
);

/* ── Concentric pulse rings — enhanced with color ────────────────── */
const PulseRings = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
    {[0, 1, 2, 3].map((i) => (
      <motion.div
        key={i}
        className="absolute rounded-full"
        style={{
          width: `${55 + i * 18}%`,
          height: `${55 + i * 18}%`,
          border: `1.5px solid`,
          borderColor: [
            "rgba(139,92,246,0.18)",
            "rgba(192,132,252,0.12)",
            "rgba(115,147,179,0.1)",
            "rgba(139,92,246,0.06)",
          ][i],
          boxShadow: [
            "0 0 30px rgba(139,92,246,0.08)",
            "0 0 20px rgba(192,132,252,0.05)",
            "0 0 15px rgba(115,147,179,0.04)",
            "0 0 10px rgba(139,92,246,0.02)",
          ][i],
        }}
        animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.15, 0.5] }}
        transition={{
          duration: 4 + i,
          repeat: Infinity,
          delay: i * 0.6,
          ease: "easeInOut",
        }}
      />
    ))}
  </div>
);

/* ── Inline progress tracker ─────────────────────────────────────── */
const ProgressTracker = ({ categories }) => (
  <motion.div
    initial={{ opacity: 0, y: 25 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay: 0.7 }}
    className="mt-6 w-full"
  >
    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2">
      {categories.map((cat, i) => (
        <motion.span
          key={i}
          className="px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer"
          style={{
            background:
              i === 0
                ? "linear-gradient(135deg, #8B5CF6, #7C3AED)"
                : "rgba(139,92,246,0.06)",
            color: i === 0 ? "#fff" : "#5B4A7C",
            border: i === 0 ? "none" : "1px solid rgba(139,92,246,0.12)",
            backdropFilter: i !== 0 ? "blur(12px)" : undefined,
          }}
          whileHover={{
            scale: 1.05,
            backgroundColor: i === 0 ? undefined : "rgba(139,92,246,0.12)",
            boxShadow: "0 4px 20px rgba(139,92,246,0.15)",
          }}
        >
          {cat}
        </motion.span>
      ))}
    </div>
  </motion.div>
);

/* ── Hero Section ────────────────────────────────────────────────── */
const HeroSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const badge = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.hero.badge",
  });
  const title = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.hero.title",
  });
  const subtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.hero.subtitle",
  });
  const cta = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.hero.cta",
  });
  const secondaryCta = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.hero.secondaryCta",
  });

  const categories = [0, 1, 2, 3, 4, 5].map((i) =>
    localizationProvider.translateByType({
      type: "Localized",
      text: `landing.hero.categories.${i}`,
    }),
  );

  const heroStats = [
    {
      value: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.hero.stats.successRate",
      }),
      label: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.hero.stats.successRateLabel",
      }),
    },
    {
      value: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.hero.stats.sessions",
      }),
      label: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.hero.stats.sessionsLabel",
      }),
    },
  ];

  return (
    <Box
      className="relative min-h-screen flex items-center overflow-hidden"
      id="home"
      sx={{ background: "transparent" }}
    >
      {/* Warm ambient blobs — richer and more vivid */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-[-15%] left-[-10%] w-[55%] h-[60%] rounded-full bg-[#E0CDF5]/60 blur-[140px]"
          animate={{ scale: [1, 1.05, 1], x: [0, 15, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-[0%] right-[-8%] w-[48%] h-[55%] rounded-full bg-[#F5E1EC]/50 blur-[130px]"
          animate={{ scale: [1, 1.08, 1], y: [0, -10, 0] }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
        <motion.div
          className="absolute bottom-[-10%] left-[20%] w-[60%] h-[50%] rounded-full bg-[#F9E8D9]/40 blur-[150px]"
          animate={{ scale: [1, 1.06, 1], x: [0, -20, 0] }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4,
          }}
        />
        <motion.div
          className="absolute bottom-[5%] right-[5%] w-[35%] h-[40%] rounded-full bg-[#D4C0F7]/30 blur-[110px]"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 6,
          }}
        />
        <motion.div
          className="absolute top-[35%] left-[35%] w-[40%] h-[40%] rounded-full bg-[#E0D8F9]/35 blur-[110px]"
          animate={{ opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Sparkle decorations — more, multi-colored */}
      <Sparkle x="12%" y="22%" size={16} delay={0} color="#8B5CF6" />
      <Sparkle x="82%" y="16%" size={12} delay={1.2} color="#C084FC" />
      <Sparkle x="68%" y="75%" size={14} delay={2.5} color="#7393B3" />
      <Sparkle x="28%" y="82%" size={10} delay={1.8} color="#EC4899" />
      <Sparkle x="55%" y="12%" size={13} delay={0.6} color="#8B5CF6" />
      <Sparkle x="92%" y="45%" size={11} delay={3.2} color="#C084FC" />
      <Sparkle x="5%" y="55%" size={9} delay={2.0} color="#7393B3" />
      <Sparkle x="40%" y="5%" size={15} delay={4.0} color="#A855F7" />

      <Container maxWidth="xl" className="relative z-10 pt-28 pb-16" dir={dir}>
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-0 px-4 md:px-8">
          {/* ── Left: Content ─────────────────────────────── */}
          <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-start max-w-2xl lg:max-w-[55%]">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <div
                className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full backdrop-blur-xl shadow-lg shadow-[#8B5CF6]/5"
                style={{
                  background: "rgba(255,255,255,0.55)",
                  border: "1px solid rgba(139,92,246,0.12)",
                }}
              >
                <motion.span
                  className="w-2 h-2 rounded-full bg-[#8B5CF6]"
                  animate={{ scale: [1, 1.4, 1], opacity: [1, 0.6, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <Typography
                  variant="body2"
                  className="text-[#5B4A7C] font-semibold tracking-wide uppercase text-xs"
                >
                  {badge}
                </Typography>
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 25, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.9, delay: 0.1 }}
            >
              <Typography
                variant="h1"
                className="text-5xl md:text-6xl lg:text-[4.5rem] font-extrabold tracking-tight leading-[1.08] mb-7"
                sx={{
                  color: "#2D1B4E",
                  fontFamily: "'Georgia', serif",
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                }}
              >
                {title}
              </Typography>
            </motion.div>

            {/* Subtitle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
            >
              <Typography className="text-lg md:text-xl text-[#6B5B7B] mb-10 leading-relaxed max-w-lg">
                {subtitle}
              </Typography>
            </motion.div>

            {/* CTA buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 items-center mb-8"
            >
              <Button
                variant="contained"
                endIcon={
                  <ArrowForwardIosIcon
                    style={{ fontSize: "0.75rem" }}
                    className={dir === "rtl" ? "rotate-180" : ""}
                  />
                }
                sx={{
                  background:
                    "linear-gradient(135deg, #7393B3 0%, #8B5CF6 100%)",
                  color: "white",
                  borderRadius: "9999px",
                  px: 4.5,
                  py: 1.7,
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow:
                    "0 8px 35px rgba(139, 92, 246, 0.3), 0 0 60px rgba(139,92,246,0.1)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5A7A9A 0%, #7C3AED 100%)",
                    boxShadow:
                      "0 12px 50px rgba(139, 92, 246, 0.4), 0 0 80px rgba(139,92,246,0.15)",
                    transform: "translateY(-3px)",
                  },
                }}
              >
                {cta}
              </Button>
              <Button
                variant="text"
                sx={{
                  borderRadius: "9999px",
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "#5B4A7C",
                  border: "1px solid rgba(139,92,246,0.15)",
                  backdropFilter: "blur(12px)",
                  background: "rgba(255,255,255,0.3)",
                  "&:hover": {
                    backgroundColor: "rgba(139,92,246,0.08)",
                    borderColor: "#8B5CF6",
                    boxShadow: "0 4px 20px rgba(139,92,246,0.12)",
                  },
                }}
              >
                {secondaryCta} &rarr;
              </Button>
            </motion.div>

            {/* Subtle info line */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-center gap-6 text-sm text-[#8B7BA0]"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#8B5CF6]" />
                <span>
                  {heroStats[0]?.value} {heroStats[0]?.label}
                </span>
              </div>
              <div className="w-px h-4 bg-[#D4C5F9]" />
              <div className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C084FC]" />
                <span>
                  {heroStats[1]?.value} {heroStats[1]?.label}
                </span>
              </div>
            </motion.div>

            {/* Category pills row */}
            <ProgressTracker categories={categories} />
          </div>

          {/* ── Right: 3D Brain Image ─────────────────────── */}
          <div className="flex-1 flex items-center justify-center relative lg:justify-end">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
              className="relative w-[320px] h-[320px] md:w-[440px] md:h-[440px] lg:w-[520px] lg:h-[520px]"
            >
              {/* Concentric pulse rings */}
              <PulseRings />

              {/* Luminous halo behind brain */}
              <motion.div
                className="absolute inset-[5%] rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(192,132,252,0.1) 30%, rgba(232,213,245,0.2) 60%, transparent 80%)",
                  filter: "blur(40px)",
                }}
                animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.9, 0.6] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Warm glow behind brain */}
              <div className="absolute inset-[8%] rounded-full bg-gradient-to-br from-[#E8D5F5]/70 via-[#D4C5F9]/50 to-[#F5E1EC]/40 blur-[70px]" />

              {/* Brain image */}
              <motion.img
                src="/static/images/hero-brain-3d.png"
                alt="3D Brain Model"
                className="relative z-10 w-full h-full object-contain drop-shadow-2xl"
                style={{
                  filter: "drop-shadow(0 0 40px rgba(139,92,246,0.15))",
                }}
                animate={{ y: [0, -12, 0] }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Floating stat card: Top Right — enhanced frosted glass */}
              <motion.div
                className="absolute top-4 -right-2 md:top-8 md:right-0 z-20"
                animate={{ y: [0, -6, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div
                  className="backdrop-blur-2xl rounded-2xl px-5 py-3.5 shadow-xl"
                  style={{
                    background: "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(232,213,245,0.5)",
                    boxShadow:
                      "0 8px 32px rgba(139,92,246,0.1), 0 0 0 1px rgba(255,255,255,0.5) inset",
                  }}
                >
                  <Typography className="text-xl md:text-2xl font-extrabold text-[#2D1B4E]">
                    {heroStats[0]?.value}
                  </Typography>
                  <Typography className="text-[10px] md:text-xs text-[#8B7BA0] font-medium">
                    {heroStats[0]?.label}
                  </Typography>
                </div>
              </motion.div>

              {/* Floating stat card: Bottom Left — enhanced frosted glass */}
              <motion.div
                className="absolute bottom-8 -left-2 md:bottom-12 md:left-0 z-20"
                animate={{ y: [0, 6, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
              >
                <div
                  className="backdrop-blur-2xl rounded-2xl px-5 py-3.5 shadow-xl"
                  style={{
                    background: "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(232,213,245,0.5)",
                    boxShadow:
                      "0 8px 32px rgba(139,92,246,0.1), 0 0 0 1px rgba(255,255,255,0.5) inset",
                  }}
                >
                  <Typography className="text-xl md:text-2xl font-extrabold text-[#2D1B4E]">
                    {heroStats[1]?.value}
                  </Typography>
                  <Typography className="text-[10px] md:text-xs text-[#8B7BA0] font-medium">
                    {heroStats[1]?.label}
                  </Typography>
                </div>
              </motion.div>

              {/* Small EEG stat card - floating right side */}
              <motion.div
                className="absolute top-[45%] -right-6 md:-right-10 z-20 hidden md:block"
                animate={{ y: [0, -4, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
              >
                <div
                  className="backdrop-blur-2xl rounded-2xl px-4 py-3 shadow-xl min-w-[140px]"
                  style={{
                    background: "rgba(255,255,255,0.6)",
                    border: "1px solid rgba(245,225,236,0.5)",
                    boxShadow:
                      "0 8px 32px rgba(139,92,246,0.08), 0 0 0 1px rgba(255,255,255,0.5) inset",
                  }}
                >
                  {/* Mini EEG wave inside card */}
                  <svg
                    viewBox="0 0 120 24"
                    className="w-full h-5 overflow-visible"
                  >
                    <motion.path
                      d="M0,12 Q10,12 15,6 T30,18 T45,4 T60,20 T75,8 T90,16 T105,10 T120,12"
                      fill="none"
                      stroke="url(#miniEEG)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 2.5, delay: 1 }}
                    />
                    <defs>
                      <linearGradient
                        id="miniEEG"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="0%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#EC4899" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </motion.div>

              {/* Category pills floating around brain */}
              {categories.slice(0, 3).map((cat, i) => {
                const positions = [
                  "top-[62%] -left-8 md:-left-14",
                  "top-[18%] -left-4 md:-left-10",
                  "bottom-[3%] right-[15%]",
                ];
                return (
                  <motion.div
                    key={i}
                    className={`absolute ${positions[i]} z-20 hidden md:block`}
                    animate={{ y: [0, i % 2 === 0 ? -5 : 5, 0] }}
                    transition={{
                      duration: 3 + i,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.5,
                    }}
                  >
                    <span
                      className="px-3.5 py-1.5 rounded-full text-xs font-medium backdrop-blur-xl text-[#5B4A7C] shadow-md"
                      style={{
                        background: "rgba(255,255,255,0.55)",
                        border: "1px solid rgba(232,213,245,0.4)",
                        boxShadow: "0 4px 16px rgba(139,92,246,0.08)",
                      }}
                    >
                      {cat}
                    </span>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default HeroSection;
